import { PatternFormat } from 'react-number-format';
import parse from 'html-react-parser';
import { AccountElementType } from '../helpers/types';
import { useEffect, useState } from 'react';
import { useNewApplicationContext } from '../providers/NewApplicationProvider';
import { useAuthContext } from '../providers/AuthProvider';
import { useTranslation } from 'react-i18next';

type PropType = {
    value: string;
    onChange: (value: string) => void;
    error: string | null;
}

const Account = (props: AccountElementType & PropType) => {
    const { value, mask, label, placeholder, onChange, replace, replaceWith, error, description, skipKarCheck } = props;
    const { formData, updateForm } = useNewApplicationContext();
    const { apiCall } = useAuthContext();
    const { t } = useTranslation();
    const [customError, setCustomError] = useState<string | null>(null);
    const [isLoading, setLoading] = useState(false);


    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const transformedValue = e.target.value.replaceAll(replace ?? ' ', replaceWith ?? '');
        onChange(transformedValue);
    };

    const maxLength = 11;

    useEffect(() => {
        // «Skru av KAR-sjekk» på feltet: ingen KAR-forespørsel (de faktureres
        // per oppslag) og ingen blokkering — alle kontonummer godtas.
        if (skipKarCheck) {
            setCustomError(null);
            setLoading(false);
            updateForm("valid_account", null);
            return;
        }

        const validateInputAndCallApi = async () => {
            if (value.length === maxLength) {
                setLoading(true);
                try {
                    const response = await apiCall({
                        action: 'checkKarAccount',
                        account: value,
                        customer: formData['orgnr']
                    });

                    // Optional chaining er nødvendig: checkKarAccount i
                    // applicationReactApi.php returnerer `['status' => 400,
                    // 'message' => ...]` UTEN `data`-nøkkel når account eller
                    // customer mangler — og `customer` er `formData['orgnr']`, som
                    // er tomt både for gavetyper uten orgnr-krav og når søker
                    // fyller kontonummer før orgnr. `response.data.data.status`
                    // kastet da TypeError, som havnet i catch nedenfor. Et manglende
                    // orgnr er et negativt oppslag, ikke en exception.
                    if (response?.data?.data?.status === 200) {
                        updateForm("valid_account", 'true');
                        setCustomError(null);
                    } else {
                        updateForm("valid_account", 'false');
                        setCustomError(t('input_account_error'))
                    }
                } catch (err) {
                    // Feiler selve KAR-kallet (nettverk, 5xx, timeout) er utfallet
                    // det samme som et negativt oppslag: vi har IKKE bekreftet at
                    // kontoen tilhører søker. Vi må derfor sette 'false', ikke la
                    // feltet stå tomt — `updateForm(..., null)` FJERNER feltet fra
                    // formData, og da slår verken `valid_account === 'true'` eller
                    // `=== 'false'` til i refine-en i schema.tsx. Resultatet var at
                    // «Bekreft likevel»-avkryssingen ikke låste opp skjemaet, og at
                    // søker satt igjen med en død «Neste»-knapp uten feilmelding.
                    console.error("API call failed:", err);
                    updateForm("valid_account", 'false');
                    setCustomError(t('input_account_error'));
                } finally {
                    setLoading(false);
                }
            } else {
                setCustomError(null);
                updateForm("valid_account", null);
                setLoading(false);
            }
        };

        validateInputAndCallApi();
    }, [value, skipKarCheck]);

    return (
        <>
            <label className="form-label">{label}</label>
            {description && <div className="text-muted small mb-2">{parse(description)}</div>}
            <div className="input-group position-relative">
                <PatternFormat
                    value={value}
                    format={mask || '0000 00 00000'}
                    mask=""
                    patternChar="0"
                    className={`form-control ${!isLoading ? (customError || error) && value ? 'is-invalid' : value ? 'is-valid' : '' : ''}`}
                    placeholder={placeholder}
                    onChange={handleChange}
                    style={{ borderRight: isLoading ? 0 : undefined }}
                />

                {isLoading && (
                    <div className="input-group-text bg-white">
                        <div className="spinner-border spinner-border-sm rounded-circle" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                )}

                {/* Displaying error or custom error */}
                {customError ? (
                    <div className="invalid-feedback">{customError}</div>
                ) : error ? (
                    <div className="invalid-feedback">{error}</div>
                ) : null}
            </div>
        </>
    );
};

export default Account;